/**
 * A collection of appView models.
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/appView';

class AppViews extends Base {
	/**
	 * @override
	 * @listens {@link Netx.Collections.AppViews#add}
	 * @listens {@link Netx.Collections.AppViews#destroyed}
	 * @listens {@link Netx.Collections.AppViews#switched}
	 * @listens {@link Netx.Collections.AppViews#switchedBack}
	 * @listens {@link Netx.Collections.AppViews#remove}
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		this.view = opts.view;

		// Hey, listen!
		// A model has been destroyed or returned to its original `viewCollection`
		// This will cleanup any other collection the model may have lived in (other than home - that must be done explicitly)
		this.listenTo(this, 'destroyed switchedBack', (model, collection) => {
			// The model has a `viewCollection` and the model's `viewCollection` is not us
			if (model.collection && model.collection._listenId !== this._listenId) {
				// Make sure it is removed from all collections that are not the model's `viewCollection`
				collection._listenId !== this._listenId && this.remove(model);
			}
		})
			// A model switched `viewCollections`
			.listenTo(this, 'switched', (model, collection) => {
				// Not the original `viewCollection` and `viewCollection` switched to is not us
				if (
					model.collection &&
					model.collection._listenId !== this._listenId &&
					collection._listenId !== this._listenId
				) {
					// Models can only live in two places at a time, and we are not one of those two places
					this.remove(model);
				}
			});
		// Are we a view queue
		// If so, manage setting and unsetting the `queued` attribute on the model when added/removed
		if (this.isQueue) {
			this.listenTo(this, 'add', (model, collection) => {
				this._listenId === collection._listenId && model.set('queued', true);
			}).listenTo(this, 'remove', (model, collection) => {
				this._listenId === collection._listenId && model.set('queued', false);
			});
		}
	}

	/**
	 * @override
	 * @param {array} models Models
	 * @param {object} opts Options
	 */
	parse(models, opts) {
		if (!_.isArray(models)) models = [models];

		_.each(models, (model) => {
			if (model instanceof Model) return;

			if (model.id == null) {
				const viewClass = model.viewClass;
				model.id = viewClass.__name__;
			}
		});
		return models;
	}

	/** @override */
	add(models, opts) {
		opts = opts || {};

		// We always merge
		// but... if by chance we have explicitly opted not to... whelp.
		if (opts.merge !== false) {
			opts.merge = true;
		}
		// Ditto
		if (opts.parse !== false) {
			opts.parse = true;
		}

		return super.add(models, opts);
	}

	/**
	 * This is a helper method meant for only use in the console while trying to debug a particular view
	 * Use in code is neither admirable or wise.
	 * @method
	 * @param {string} templateKey Template key
	 * @return {module:core/lib/netx-view} Instance of netx-view
	 */
	findByTemplateKey(templateKey) {
		console.warn(
			'Do not use the `findByTemplateKey` method in production! The method `findByTemplateKey` is not supported',
		);

		/**
		 * @function
		 * @param {module:core/lib/netx-view} view View
		 * @return {module:core/lib/netx-view} Instance of netx-view
		 */
		const findView = (view) => {
			if (view.templateKey === templateKey) {
				return view;
			}

			let found = false;
			if (view.viewCollection) {
				found = findInViews(view.viewCollection.pluck('view'));
				if (found) return found;
			}
			if (view.childViews) {
				found = findInViews(_.values(view.childViews));
				if (found) return found;
			}
			if (view.subViews) {
				found = findInViews(_.values(view.subViews));
				if (found) return found;
			}

			return false;
		};

		/**
		 * @function
		 * @param {array} views Views
		 * @return {module:core/lib/netx-view} Instance of netx-view
		 */
		const findInViews = (views) => {
			for (let i = 0, c = views.length, view, found; i < c; i++) {
				view = views[i];
				found = findView(view);
				if (found) return found;
			}
			return false;
		};

		return findInViews(this.pluck('view'));
	}

	/**
	 * Get transient view (if there is one)
	 * @method
	 * @return {boolean|Netx.Models.AppView}
	 */
	getTransient() {
		var transient = false;
		for (var i = 0, c = this.length; i < c; i++) {
			transient = !this.at(i).isMyOriginalCollection(this) ? this.at(i) : false;
			if (transient) {
				break;
			}
		}
		return transient;
	}

	/**
	 * Overriding to document this: Never use reset on this collection after it has been populated with models - always use
	 * `set([])` if you are looking to clear it out properly. We will get all the proper events rather than a single `reset` event.
	 * Nothing about how this event works is overriden.
	 * @override
	 */
	reset(models, opts) {
		// I would like to warn you about using reset manually on this - but backbone sends a reset call off the bat
		// to new collections. Do not want to throw up bad warnings.
		return super.reset(models, opts);
	}

	/** @override */
	set(models, opts = {}) {
		// Always parse unless explicitly told not to
		opts.parse = opts.parse !== false;
		return super.set(models, opts);
	}

	/**
	 * @method
	 * @param {Netx.Models.AppView} model App view model
	 * @param {Netx.Collections.AppViews} collection The new `viewCollection`
	 * @param {object} opts Add to collection options
	 */
	switchTo(model, collection, opts = {}) {
		// You should never be trying to `switchTo` the original `viewCollection`
		if (collection !== this) {
			// Store this so that we can know the current context of the `viewCollection`
			model.currentCollection = collection;
			collection.add(model, opts);
			// Let the  model know it has switched `viewCollections`
			model.trigger('switched', model, collection);
		}
		// But in the event you are - just remove it from the `currentCollection` and let events do the rest
		else {
			model.switchBack();
		}

		return this;
	}

	/** @type {string} */
	static get __name__() {
		return 'AppViews';
	}
}

export default AppViews;
